import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import {
  BANNER_PRESETS,
  resolveBannerVisual,
  visualBackground,
  BANNER_AUDIENCES,
  BANNER_PLATFORMS,
  BANNER_ICONS,
  Banner,
} from '../../../api/banners';
import { BannersTableProps } from '../types';

const formatDate = (date: string | null) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Почему баннер не показывается, даже когда включён. Пользователь админки
 * иначе будет гадать, отчего акция «не работает».
 */
function getStatus(banner: Banner): { label: string; tone: 'live' | 'muted' | 'warn' } {
  if (!banner.is_active) return { label: 'Выключен', tone: 'muted' };

  const now = Date.now();
  if (banner.starts_at && new Date(banner.starts_at).getTime() > now) {
    return { label: 'Запланирован', tone: 'warn' };
  }
  if (banner.ends_at && new Date(banner.ends_at).getTime() < now) {
    return { label: 'Истёк', tone: 'muted' };
  }
  return { label: 'Показывается', tone: 'live' };
}

export function BannersTable({
  isDark,
  banners,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
}: BannersTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const headCls = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');

  return (
    <Table>
      <TableHeader>
        <TableRow
          className={cn(
            isDark
              ? 'border-slate-700 hover:bg-slate-800/50'
              : 'border-slate-100 hover:bg-slate-50',
          )}
        >
          <TableHead className={headCls}>Баннер</TableHead>
          <TableHead className={cn(headCls, 'hidden lg:table-cell')}>Оформление</TableHead>
          <TableHead className={cn(headCls, 'hidden md:table-cell')}>Кому</TableHead>
          <TableHead className={headCls}>Приоритет</TableHead>
          <TableHead className={cn(headCls, 'hidden lg:table-cell')}>Период</TableHead>
          <TableHead className={headCls}>Показы / CTR</TableHead>
          <TableHead className={headCls}>Статус</TableHead>
          <TableHead className={headCls}>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {banners.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} className="py-12">
              <div className="flex flex-col items-center gap-2">
                <ImageIcon
                  className={cn('h-8 w-8', isDark ? 'text-slate-600' : 'text-slate-300')}
                />
                <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  Баннеров пока нет
                </p>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          banners.map((banner) => {
            const status = getStatus(banner);
            const preset = BANNER_PRESETS.find((p) => p.value === banner.preset);
            // Свотч в светлой теме, с учётом ручных цветов, если они заданы.
            const swatch = resolveBannerVisual(banner.preset, false, banner.custom_style);
            const icon = BANNER_ICONS.find((i) => i.value === banner.icon);

            return (
              <TableRow
                key={banner.id}
                className={cn(
                  isDark
                    ? 'border-slate-700 hover:bg-slate-800/50'
                    : 'border-slate-100 hover:bg-slate-50',
                )}
              >
                <TableCell>
                  <div className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                    {banner.name}
                  </div>
                  <div className={cn('text-xs mt-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    {banner.title?.ru || banner.title?.uz || banner.title?.kiril || '—'}
                  </div>
                </TableCell>

                <TableCell className="hidden lg:table-cell">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex h-6 w-6 items-center justify-center rounded-md text-xs"
                      style={{
                        background: visualBackground(swatch),
                        color: swatch.fg,
                      }}
                      title={preset?.label}
                    >
                      {icon?.label.split(' ')[0] ?? '•'}
                    </span>
                    <span className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                      {preset?.label ?? banner.preset}
                    </span>
                  </div>
                </TableCell>

                <TableCell className="hidden md:table-cell">
                  <div className={cn('text-xs', isDark ? 'text-slate-300' : 'text-slate-700')}>
                    {BANNER_AUDIENCES.find((a) => a.value === banner.audience)?.label}
                  </div>
                  {banner.platform !== 'all' && (
                    <Badge variant="outline" className="mt-1 text-[10px]">
                      {BANNER_PLATFORMS.find((p) => p.value === banner.platform)?.label}
                    </Badge>
                  )}
                </TableCell>

                <TableCell>
                  <span className={cn('font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
                    {banner.priority}
                  </span>
                </TableCell>

                <TableCell className={cn('hidden lg:table-cell text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  {formatDate(banner.starts_at)} — {formatDate(banner.ends_at)}
                </TableCell>

                <TableCell>
                  <div className={cn('text-sm', isDark ? 'text-white' : 'text-[#111111]')}>
                    {banner.impressions_count}
                  </div>
                  <div className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    {banner.clicks_count} кликов · {(banner.ctr * 100).toFixed(1)}%
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={banner.is_active}
                      onCheckedChange={() => onToggleActive(banner)}
                    />
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px]',
                        status.tone === 'live' && 'border-emerald-500 text-emerald-500',
                        status.tone === 'warn' && 'border-amber-500 text-amber-500',
                        status.tone === 'muted' && 'border-slate-400 text-slate-400',
                      )}
                    >
                      {status.label}
                    </Badge>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => onEdit(banner)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(banner.id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
